import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import {
  API,
  AssetCategoryFullDTO,
  AssetDTO,
  AssetFileDTO,
  AssetFullDTO,
  AssetRepositoryFullDTO,
  ClientCullDTO,
  Pagination,
} from '../api/api';
import { SbDateTime } from '../api/api-date-time';
import { AssetCategoryEditComponent } from '../../pages/assets/datasets/asset-category-edit/asset-category-edit.component';
import { AssetCategoryListComponent } from '../../pages/assets/datasets/asset-category-list/asset-category-list.component';
import { ModalService } from '../gui/modal/modal.service';
import { SbSplitButton } from '../global/global-interfaces';
import { finalize, switchMap, take } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ErrorService } from '../error.service';
import { FilterListComponent } from '../../pages/datasets/filter-list/filter-list.component';
import { FilterEditComponent } from '../../pages/datasets/filter-edit/filter-edit.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StringTransformationService } from '../transformation-helper/string-transformation.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelConfig } from '@ngx-seatback/layout';
import { RoutingID } from '../../routes/sb-routing-enum';
import { PermissionService } from '../permission.service';

type SplitButtonActionAssets = 'edit' | 'delete';
type SplitButtonActionRepositories = 'edit' | 'assetCategories' | 'assets' | 'delete';


@Injectable({
  providedIn: 'root',
})
export class AssetService {
  public readonly permissionService = inject( PermissionService);
  private readonly api = inject(API);
  private readonly modalService = inject(ModalService);
  private readonly translate = inject(TranslateService);
  private readonly errorService = inject(ErrorService);
  private readonly stringTransformation = inject(StringTransformationService);
  private readonly destroyRef = inject(DestroyRef);

  // global variables
  loading = signal<boolean>(true);
  debounceTimer: ReturnType<typeof setTimeout> = null;
  subs: Subscription[] = [];
  newTag: string = '';
  showSideBar: boolean = false;
  duplicateResolutions: string[] = [];

  // table wrapper variables
  paginationRepositories: BehaviorSubject<Pagination> | undefined;
  paginationAssets: BehaviorSubject<Pagination> | undefined;
  repositories: AssetRepositoryFullDTO[] = [];
  assets: AssetFullDTO[] = [];
  private _selectedRepository: AssetRepositoryFullDTO = null;
  private _selectedAsset: AssetFullDTO = null;

  // filter variables
  exactMatchRepositories: SearchOptions = 'contains';
  searchRepositories: string | null = null;
  exactMatchAssets: SearchOptions = 'contains';
  searchAssets: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    hideTagSection: true,
    button: { visibleWithoutChanges: true },
  };

  set selectedRepository(val: AssetRepositoryFullDTO) {
    this._selectedRepository = val;
  }

  get selectedRepository(): AssetRepositoryFullDTO {
    return this._selectedRepository;
  }

  set selectedAsset(val: AssetFullDTO) {
    this._selectedAsset = val;
    if (this._selectedAsset?.assetFiles?.length > 1) {
      const resolutions: string[] = [];
      this._selectedAsset.assetFiles.forEach(assetFile => resolutions.push(assetFile?.resolution?.id));
      this.duplicateResolutions = Array.from(
        new Set<string>(resolutions.filter((resolution, index) => resolutions.indexOf(resolution) !== index))
      );
    } else {
      this.duplicateResolutions = [];
    }
  }

  get selectedAsset(): AssetFullDTO {
    return this._selectedAsset;
  }

  handleDateChangeAssets($event: { fromDate: SbDateTime; toDate: SbDateTime }): void {
    if ($event) {
      if ($event.fromDate) {
        this.selectedAsset.shareStart = $event.fromDate;
      }
      if ($event.toDate) {
        this.selectedAsset.shareEnd = $event.toDate;
      }
    }
  }

  deleteCategory(): void {
    this.selectedAsset.category = null;
  }

  createCategory(): void {
    if (this.selectedAsset.repository) {
      const category: AssetCategoryFullDTO = new AssetCategoryFullDTO();
      category.repository = this.selectedAsset.repository;
      this.modalService
        .showNavigationModal({
          entryComponent: { value: AssetCategoryEditComponent, data: { assetCategory: category, repositoryId: category.repository.id } },
          title: null,
          modalOptions: { windowClass: 'M' },
        })
        .then((result: AssetCategoryFullDTO) => {
          if (result) {
            this.api
              .dm_assets_AssetRepository_createAssetCategory(this.selectedAsset.repository.id, result)
              .pipe(take(1))
              .subscribe({
                next: (cat: AssetCategoryFullDTO) => {
                  this.selectedAsset.category = cat;
                },
              });
          } else {
            this.selectedAsset.category = null;
          }
        })
        .catch(() => {});
    }
  }

  selectCategory(): void {
    this.modalService
      .showNavigationModal({
        entryComponent: { value: AssetCategoryListComponent, data: { repoId: this.selectedAsset?.repository?.id } },
        title: null,
        modalOptions: { windowClass: 'M' },
      })
      .then((result: AssetCategoryFullDTO) => {
        if (result) {
          this.selectedAsset.category = result;
        } else {
          this.selectedAsset.category = null;
        }
      })
      .catch(() => {});
  }

  deleteAssetFile(assetFile: AssetFileDTO): void {
    this.api
      .dm_assets_AssetFile_deleteFile(assetFile.id)
      .pipe(
        switchMap(() => this.api.dm_assets_Asset_assetRetrieveById(this.selectedAsset.id)),
        take(1)
      )
      .subscribe({
        next: (asset: AssetFullDTO) => {
          this.selectedAsset = asset;
        },
      });
  }

  removeTag(tag: string): void {
    if (this.selectedAsset?.tags?.includes(tag)) {
      const index = this.selectedAsset.tags.indexOf(tag);
      this.selectedAsset.tags.splice(index, 1);
    }
  }

  addTag(): void {
    if (!this.selectedAsset.tags) {
      this.selectedAsset.tags = [];
    }
    this.selectedAsset.tags.push(this.newTag);
    this.newTag = '';
  }

  refreshFromServerAssets(): void {
    if (!this.selectedRepository) {
      return;
    }
    const sub = this.api
      .dm_assets_AssetRepository_assetsRetrieveAll(
        this.selectedRepository.id,
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatchAssets, this.searchAssets),
        },
        this.paginationAssets
      )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe((assets: AssetFullDTO[]): void => {
        this.assets = assets;
        this.assets = this.buildSplitButtonsAssets(this.assets);
      });
    this.subs.push(sub);
  }

  requestDebouncedAssets(options?: BehaviorSubject<Pagination>): void {
    this.loading.set(true);
    if (options) {
      this.paginationAssets = options;
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.subs.forEach(sub => sub.unsubscribe());
    this.subs = [];

    this.debounceTimer = setTimeout(() => {
      this.refreshFromServerAssets();
    }, 500);
  }

  deleteAsset(asset: AssetDTO): void {
    this.modalService
      .confirm({
        title: this.translate.instant('lang.globals.removeAsset', { asset: asset.name }),
        message: this.translate.instant('lang.globals.removeAssetQuestion', { asset: asset.name }),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          this.api
            .dm_assets_Asset_deleteAsset(asset.id)
            .pipe(take(1))
            .subscribe({
              next: () => {
                this.resetViewAssets();
              },
              error: error => {
                this.errorService.errorHandler(error);
              },
            });
        }
      });
  }

  updateAsset(asset: AssetFullDTO): void {
    if (!asset.id) {
      this.api
        .dm_assets_AssetRepository_createAssetInRepository(this.selectedRepository.id, asset)
        .pipe(take(1))
        .subscribe({
          next: (createdAsset: AssetFullDTO) => {
            this.selectedAsset = createdAsset;
            this.requestDebouncedAssets();
          },
          error: error => {
            this.errorService.errorHandler(error);
          },
        });
    } else {
      this.api
        .dm_assets_Asset_editAsset(asset.id, asset)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.resetViewAssets();
          },
          error: error => {
            this.errorService.errorHandler(error);
          },
        });
    }
  }

  toggleSideBar() {
    this.showSideBar = !this.showSideBar;
  }

  resetViewAssets(refresh: boolean = true) {
    if (this.showSideBar) {
      this.toggleSideBar();
    }
    this.selectedAsset = null;
    this.newTag = '';
    if (refresh) {
      this.requestDebouncedAssets();
    }
  }

  addAsset() {
    this.selectedAsset = new AssetFullDTO();
    this.selectedAsset.repository = this.selectedRepository;
    this.toggleSideBar();
  }

  editAsset(asset: AssetFullDTO) {
    this.selectedAsset = asset;
    this.toggleSideBar();
  }

  buildSplitButtonsAssets<T extends { id: any }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonActionAssets>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonActionAssets>[] };
    });
    result.forEach(item => {
        item.sbButtons.push({
          icon: 'details',
          action: 'edit',
          tooltip: 'lang.globals.details',
        });
        item.sbButtons.push({
          icon: 'trash',
          iconClass: 'hover:!tw-bg-sbRed-500 *:tw-text-sbRed-500',
          action: 'delete',
          tooltip: 'lang.globals.delete',
        });
    });
    return result;
  }

  resolveSplitButtonActionAssets(asset: AssetFullDTO, $event: SplitButtonActionAssets) {
    switch ($event) {
      case 'edit':
        this.editAsset(asset);
        break;
      case 'delete':
        this.deleteAsset(asset);
        break;
      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }

  refreshFromServerRepositories() {
    this.api
      .dm_assets_AssetRepository_retrieveAll(
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatchRepositories, this.searchRepositories),
        },
        this.paginationRepositories
      )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (repos: AssetRepositoryFullDTO[]) => {
          this.repositories = repos;
          this.repositories = this.buildSplitButtonsRepositories(this.repositories);
        },
        error: error => {
          this.errorService.errorHandler(error);
        },
      });
  }

  requestDebouncedRepositories(options?: BehaviorSubject<Pagination>) {
    this.loading.set(true);
    if (options) {
      this.paginationRepositories = options;
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.subs.forEach(sub => sub.unsubscribe());
    this.subs = [];

    this.debounceTimer = setTimeout(() => {
      this.refreshFromServerRepositories();
    }, 500);
  }

  addRepository() {
    this.selectedRepository = new AssetRepositoryFullDTO();
    this.toggleSideBar();
  }

  editRepo(repo: AssetRepositoryFullDTO) {
    this.selectedRepository = repo;
    this.toggleSideBar();
  }

  deleteRepository(repository: AssetRepositoryFullDTO) {
    this.modalService
      .confirm({
        title: this.translate.instant('lang.globals.removeRepository', { repo: repository.name }),
        message: this.translate.instant('lang.globals.removeRepositoryQuestion', { repo: repository.name }),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          this.api
            .dm_assets_AssetRepository_deleteRepository(repository.id)
            .pipe(take(1))
            .subscribe({
              next: () => {
                this.resetViewRepositories();
              },
            });
        }
      });
  }

  updateRepository(repo: AssetRepositoryFullDTO) {
    if (!repo.id) {
      this.api
        .dm_assets_AssetRepository_createRepository(repo)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.resetViewRepositories();
          },
        });
    } else {
      this.api
        .dm_assets_AssetRepository_editAssetRepository(repo.id, repo)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.resetViewRepositories();
          },
        });
    }
  }

  resetViewRepositories(refresh: boolean = true) {
    if (this.showSideBar) {
      this.toggleSideBar();
    }
    this.selectedRepository = null;
    if (refresh) {
      this.requestDebouncedRepositories();
    }
  }

  handleDateChangeRepositories($event: { fromDate: SbDateTime; toDate: SbDateTime }) {
    if ($event) {
      if ($event.fromDate) {
        this.selectedRepository.shareStart = $event.fromDate;
      }
      if ($event.toDate) {
        this.selectedRepository.shareEnd = $event.toDate;
      }
    }
  }

  buildSplitButtonsRepositories<T extends { id: any }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonActionRepositories>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonActionRepositories>[] };
    });
    result.forEach(item => {
      if (this.permissionService.hasPermissions([this.permissionService.Permission.asset_repository_edit])) {
        item.sbButtons.push({
          icon: 'details',
          action: 'edit',
          tooltip: 'lang.globals.details',
        });
      }
      // TODO add Permission
      item.sbButtons.push({
        icon: 'category',
        tooltip: 'lang.globals.assetCategories',
        link: RoutingID.assetCategories,
        pathParams: [{id: 'repositoryID', value: item?.id}],
      });
      // TODO add Permission
      item.sbButtons.push({
        icon: 'photos',
        tooltip: 'lang.globals.assets',
        link: RoutingID.assets,
        pathParams: [{id: 'repositoryID', value: item?.id}],
      });
      if (this.permissionService.hasPermissions([this.permissionService.Permission.asset_repository_delete])) {
        item.sbButtons.push({
          icon: 'trash',
          iconClass: 'hover:!tw-bg-sbRed-500 *:tw-text-sbRed-500',
          action: 'delete',
          tooltip: 'lang.globals.delete',
        });
      }
    });
    return result;
  }

  resolveSplitButtonActionRepositories(repository: AssetRepositoryFullDTO, $event: SplitButtonActionAssets) {
    switch ($event) {
      case 'edit':
        this.editRepo(repository);
        break;
      case 'delete':
        this.deleteRepository(repository);
        break;
      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }

  selectClientCull() {
    this.modalService
      .showNavigationModal({
        entryComponent: { value: FilterListComponent, data: { preselectType: 'ClientCull' } },
        title: null,
        modalOptions: { windowClass: 'bigXL' },
      })
      .then((result: ClientCullDTO) => {
        if (result) {
          this.selectedRepository.clientCull = result;
        } else {
          this.selectedRepository.clientCull = null;
        }
      })
      .catch(() => {});
  }

  createClientCull() {
    this.modalService
      .showNavigationModal({
        entryComponent: { value: FilterEditComponent, data: { cullType: 'ClientCull', companyDisabled: true } },
        title: null,
        modalOptions: { windowClass: 'bigXL' },
      })
      .then((result: ClientCullDTO) => {
        if (result) {
          this.selectedRepository.clientCull = result;
        } else {
          this.selectedRepository.clientCull = null;
        }
      })
      .catch(() => {});
  }

  deleteClientCull() {
    this.selectedRepository.clientCull = null;
  }

  retrieveRepo(repoId: number): void {
    if (!repoId) {
      return;
    }
    this.api
      .dm_assets_AssetRepository_retrieveById(repoId)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (repo: AssetRepositoryFullDTO) => {
          this.selectedRepository = repo;
        },
      });
  }

  refreshSelectedAsset() {
    if (!this.selectedAsset) {
      return;
    }
    this.api
      .dm_assets_Asset_assetRetrieveById(this.selectedAsset.id)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (asset: AssetFullDTO) => {
          this.selectedAsset = asset;
        },
      });
  }

  setToPreview(assetFile: AssetFileDTO) {
    this.selectedAsset.preview = assetFile;
  }
}
